import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { formatCurrency } from 'src/shared/utils/format-currency'

import type { PortfolioImage, PortfolioItem } from '../../../schemas/portfolio'

type PortfolioCardProps = {
  item: PortfolioItem
  username: string
  isRealtor: boolean
}

const ImageGrid = ({ images }: { images: PortfolioImage[] }) => {
  if (images.length === 0) {
    return null
  }

  if (images.length === 1) {
    return (
      <div className="flex flex-row gap-3">
        <img
          src={images[0].photoUrl || undefined}
          alt={images[0].key || ''}
          className="w-full rounded-[10px] object-cover"
        />
      </div>
    )
  }

  if (images.length === 2) {
    return (
      <div className="flex flex-row gap-3">
        <img
          src={images[0].photoUrl || undefined}
          alt={images[0].key || ''}
          className="flex-1 rounded-[10px] object-cover"
        />
        <img
          src={images[1].photoUrl || undefined}
          alt={images[1].key || ''}
          className="flex-1 rounded-[10px] object-cover"
        />
      </div>
    )
  }

  if (images.length >= 3) {
    return (
      <div className="flex flex-row gap-3">
        <img
          src={images[0].photoUrl || undefined}
          alt={images[0].key || ''}
          className="aspect-square min-w-[65%] max-w-[65%] rounded-[10px] object-cover"
        />
        <div className="flex flex-col gap-3">
          <img
            src={images[1].photoUrl || undefined}
            alt={images[1].key || ''}
            className="aspect-square w-full rounded-[10px] object-cover"
          />
          <img
            src={images[2].photoUrl || undefined}
            alt={images[2].key || ''}
            className="aspect-square w-full rounded-[10px] object-cover"
          />
        </div>
      </div>
    )
  }

  return null
}

const PortfolioCard = ({ item, username, isRealtor }: PortfolioCardProps) => {
  const navigate = useNavigate()
  const onClick = () => {
    navigate(paths.users.publicProfile.portfolio.single.getHref(username, item.id.toString()))
  }

  const listingPrice = item.listingPrice ? `$${formatCurrency(item.listingPrice)}` : null
  const bedrooms = item.bedrooms ? `${item.bedrooms} beds` : null
  const bathrooms = item.bathrooms ? `${item.bathrooms} bathrooms` : null
  const squareFeet = item.squareFeet ? `${item.squareFeet} sqft` : null
  const description = [listingPrice, bedrooms, bathrooms, squareFeet].filter(Boolean).join(' • ')

  return (
    <button
      onClick={onClick}
      className="flex min-w-[250px] max-w-[calc(100vw-40px)] flex-col gap-[15px] rounded-[12px] border-[0.5px] border-[#efefef] bg-white p-[15px] text-left md:min-w-[280px] md:max-w-none"
      style={{
        boxShadow: '0px 3px 10px 1px rgba(27, 48, 41, 0.03)',
      }}
    >
      <ImageGrid images={item.images} />
      <div className="flex flex-col gap-1 px-2">
        <div className="line-clamp-1 break-words text-[16px] font-bold leading-[1.3] md:text-[18px]">{item.title}</div>
        <div className="line-clamp-2 break-words text-[14px] leading-[1.4] text-muted md:text-[15px]">
          {isRealtor ? description : item.description}
        </div>
      </div>
    </button>
  )
}

export default PortfolioCard
